import React, {
  Fragment, useState, useEffect, useMemo, useCallback,
} from 'react'
import PropTypes from 'prop-types'
import { useSelector, shallowEqual, useDispatch } from 'react-redux'
import Head from 'next/head'
import {
  AdSlot,
  EpisodeInfo,
  GuidanceText,
  SocialShares,
  LoginDialog,
  VODPlayer,
} from '..'
import {
  API_PROGRAMME_OBJECT,
  EPISODE_INFO_OBJECT,
  ERROR_OBJECT,
} from '../../utils/types'
import errorRedirect from '../../utils/errorRedirect'
import PaywallNotice from '../PaywallNotice'
import { ClientOnly } from '../SSRSwitch'
import styles from './styles'
import paywallBgImage from '../PaywallNotice/image.png'
import { setGuidanceAccepted } from '../../actions'

const EpisodePage = React.memo((props) => {
  const episodeData = useSelector(state => state.episode.currentEpisode, shallowEqual)
  const users = useSelector(state => state.user, shallowEqual)
  const [playerHasError, setPlayerHasError] = useState(false)
  const dispatch = useDispatch()

  const {
    error,
    currentUrl,
    currentDomain,
    redirectUrl,
    fromThirdParty,
    videoQueryParams,
    isLoading,
    currentlySubscribed,
    onPostcodeSubmit,
    contentType,
  } = props

  const adSlotProps = useMemo(() => ({
    title: episodeData?.programme?.guid || episodeData?.episodeInfo?.name,
    programCategory: episodeData?.episodeInfo?.programme?.genre,
    contentProvider: episodeData?.episodeInfo?.programme?.distributor,
    categoryAssoc: episodeData?.episodeInfo?.programme?.subGenre,
  }), [episodeData])

  useEffect(() => {
    if (episodeData?.programme?.svodExclusive && users.currentlySubscribed === false) {
      window.location = `/summary/${episodeData.programme.guid}`
    }
  }, [users.currentlySubscribed, episodeData])

  useEffect(() => {
    if (error && [404, 500].includes(error.httpStatus)) {
      errorRedirect()
    }
  }, [error])

  const playerProps = useMemo(() => ({
    ...props,
    videoQueryParams: videoQueryParams || {},
  }), [props, videoQueryParams])

  const thirdPartyRedirect = useCallback((url) => {
    if (typeof window !== 'undefined') {
      window.location.assign(url)
    }
  }, [])

  if (playerProps?.error?.httpStatus) {
    playerProps.error = playerProps.error.code
  }

  const episodeInfo = episodeData?.episodeInfo || props.episodeInfo || {}
  const {
    name = '',
    title = '',
    programmeGuid = '',
    sportsExclusive = false,
  } = episodeInfo

  const showPlayerError = error && error.code === 'NOT_LOGGED_IN'

  const handleErrorStateChange = useCallback((playerError) => {
    setPlayerHasError(Boolean(playerError))
  }, [])

  return (
    <Fragment>
      {fromThirdParty && showPlayerError && thirdPartyRedirect(redirectUrl)}
      {!fromThirdParty && (
        <>
          {name && title && (
            <Head>
              <title>{`${name} | ${title}`}</title>
            </Head>
          )}
          <GuidanceText {...episodeInfo} />
          <div className="vod-content-wrapper">
            <div className="vod-content">
              <div className="episode-info-container">
                <div className="episode-info-inner">
                  <EpisodeInfo isLoading={isLoading} {...episodeInfo} />
                  {!currentlySubscribed && !isLoading && (
                    <div className="mpu-container">
                      <AdSlot
                        key={`ad-${programmeGuid}`}
                        type="medium-rectangle"
                        programmeOrChannelGuid={programmeGuid}
                        path="episode"
                        adSlotProps={adSlotProps}
                      />
                    </div>
                  )}
                </div>
              </div>
              <div className="player-container">
                {showPlayerError && (
                  <LoginDialog
                    center
                    currentDomain={currentDomain}
                    currentUrl={currentUrl}
                  />
                )}
                {!showPlayerError && (!sportsExclusive || (sportsExclusive && users.subscriptionType === 'premier_sports')) && users.gettingDetails && (
                  <div
                    className="player-container-inner"
                    data-testid="player-container-inner"
                    style={{ position: playerHasError ? '' : 'relative' }}
                  >
                    {(episodeData || playerProps.error) && !showPlayerError && (
                      <VODPlayer
                        onErrorStateChange={handleErrorStateChange}
                        error={playerProps.error}
                        guidanceText={episodeInfo.guidanceText}
                        setGuidanceAccepted={() => {
                          // dispatch(setGuidanceAccepted(episodeData.programme.guid))
                          dispatch(setGuidanceAccepted('all'))
                        }}
                        setPinAccepted={props.setPinAccepted}
                        onPostcodeSubmit={onPostcodeSubmit}
                        contentType={contentType}
                      />
                    )}
                  </div>
                )}
                {!showPlayerError && sportsExclusive && users.subscriptionType !== 'premier_sports' && users.gettingDetails && (
                  <ClientOnly>
                    <div className="no-player">
                      <PaywallNotice />
                      <div className="concurrent-notice-bgimage-wrapper">
                        {/* eslint-disable-next-line jsx-a11y/alt-text */}
                        <img src={paywallBgImage} className="concurrent-notice-bgimage" />
                        <div className="concurrent-notice-bgimage-overlay" />
                      </div>
                    </div>
                  </ClientOnly>
                )}
              </div>
            </div>
            <SocialShares title={title} url={props.currentUrl} />
          </div>
        </>
      )}
      <style jsx>{styles}</style>
    </Fragment>
  )
}, (prevProps, nextProps) => (
  prevProps.error === nextProps.error
    && prevProps.isLoading === nextProps.isLoading
    && prevProps.currentlySubscribed === nextProps.currentlySubscribed
    && prevProps.currentUrl === nextProps.currentUrl
    && prevProps.currentDomain === nextProps.currentDomain
    && prevProps.redirectUrl === nextProps.redirectUrl
    && prevProps.fromThirdParty === nextProps.fromThirdParty
    && prevProps.programme === nextProps.programme
    && JSON.stringify(prevProps.videoQueryParams) === JSON.stringify(nextProps.videoQueryParams)
))

EpisodePage.propTypes = {
  programme: API_PROGRAMME_OBJECT,
  episodeInfo: EPISODE_INFO_OBJECT,
  title: PropTypes.string,
  error: ERROR_OBJECT,
  errorMessage: PropTypes.string,
  contentType: PropTypes.string,
  currentUrl: PropTypes.string.isRequired,
  currentDomain: PropTypes.string.isRequired,
  currentlySubscribed: PropTypes.bool,
  redirectUrl: PropTypes.string.isRequired,
  fromThirdParty: PropTypes.bool,
  svodExclusive: PropTypes.bool,
  videoQueryParams: PropTypes.object,
  isLoading: PropTypes.bool,
  setPinAccepted: PropTypes.func, // Added prop type
  encodedPin: PropTypes.string, // Added prop type
  onPostcodeSubmit: PropTypes.func,
}

EpisodePage.defaultProps = {
  programme: {},
  episodeInfo: {},
  title: null,
  error: null,
  errorMessage: null,
  contentType: 'episode',
  currentlySubscribed: false,
  fromThirdParty: false,
  svodExclusive: false,
  videoQueryParams: {},
  isLoading: false,
  setPinAccepted: () => {},
  encodedPin: null,
  onPostcodeSubmit: () => {},
}

export default EpisodePage
